import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Calendar, Zap, CheckCircle, MessageCircle } from 'lucide-react';
import { validateForm, contactFormSchema, sanitizeInput } from '../../utils/validation';
import { announceToScreenReader } from '../../utils/accessibility';
import { buildWhatsappLink } from '../../utils/whatsapp';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
    isEmergency: false
  });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const services = [
    'Sports Physiotherapy',
    'Orthopedic Rehabilitation',
    'Neurological Physiotherapy',
    'Pediatric Care',
    'Geriatric Care',
    'Corporate Wellness',
    'Home Visits',
    'General Consultation'
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM'
  ];

  const whatsappUrl = buildWhatsappLink({
    name: formData.name,
    service: formData.service || 'General Consultation',
    preferredDate: formData.preferredDate || 'Soonest available',
    preferredTime: formData.preferredTime || 'Any time',
    message: formData.message,
    emergency: formData.isEmergency,
    context: 'Website Contact',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setErrors({});
    
    // Sanitize input data
    const sanitizedData = Object.entries(formData).reduce((acc, [key, value]) => {
      acc[key] = typeof value === 'string' ? sanitizeInput(value) : value;
      return acc;
    }, {} as Record<string, any>);
    
    // Validate form
    const validationErrors = validateForm(sanitizedData, contactFormSchema);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      announceToScreenReader('Please correct the errors in the form');
      
      // Focus on first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      errorElement?.focus();
      return;
    }
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubmitStatus('success');
      announceToScreenReader('Appointment request submitted successfully');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        preferredDate: '',
        preferredTime: '',
        message: '',
        isEmergency: false
      });
    } catch (error) {
      setSubmitStatus('error');
      announceToScreenReader('Failed to submit appointment request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const getFieldError = (fieldName: string): string | undefined => {
    return errors[fieldName]?.[0];
  };

  const hasFieldError = (fieldName: string): boolean => {
    return !!errors[fieldName];
  };

  return (
    <section 
      id="contact" 
      className="py-16 sm:py-20 bg-gray-50 scroll-mt-28 sm:scroll-mt-32 lg:scroll-mt-40"
      role="region"
      aria-labelledby="contact-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            id="contact-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6"
            role="heading"
            aria-level={2}
          >
            Book Your Appointment
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Take the first step towards better health. Schedule your consultation with 
            our expert physiotherapists today.
          </p>
        </div>

        {/* Primary WhatsApp CTA */}
        <div className="mb-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-colors duration-200 focus:ring-2 focus:ring-green-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
          >
            <MessageCircle className="w-5 h-5" /> Book via WhatsApp (Instant)
          </a>
          <a
            href="tel:+917974434671"
            className="inline-flex items-center justify-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-colors duration-200 focus:ring-2 focus:ring-blue-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
          >
            <Phone className="w-5 h-5" /> Call Clinic
          </a>
        </div>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div 
            className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span>Your appointment request has been submitted successfully! We'll contact you soon.</span>
            </div>
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div 
            className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800"
            role="alert"
            aria-live="polite"
          >
            <span>Failed to submit your request. Please try again or call us directly.</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Information */}
          <div className="space-y-8" role="complementary" aria-labelledby="contact-info-heading">
            <div>
              <h3 
                id="contact-info-heading"
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-6"
                role="heading"
                aria-level={3}
              >
                Get in Touch
              </h3>
              
              {/* Contact Details */}
              <div className="space-y-6" role="list">
                <div className="flex items-start" role="listitem">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full mr-3 sm:mr-4 flex-shrink-0" aria-hidden="true">
                    <Phone className="w-6 h-6 text-blue-600" aria-hidden="true" />
                 </div>
                   <div>
                    <h4 className="font-semibold text-gray-900 mb-1" role="heading" aria-level={4}>Phone</h4>
                    <p className="text-gray-600">
                      <a href="tel:+917974434671" className="hover:underline focus:underline">+91 7974434671</a>
                    </p>
                    <a 
                      href="https://wa.me/917974434671"
                      className="text-green-600 hover:text-green-700 font-semibold mt-1 inline-block focus:underline"
                      aria-label="WhatsApp us"
                    >
                      WhatsApp Now
                    </a>
                  </div>
                </div>

                <div className="flex items-start" role="listitem">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full mr-3 sm:mr-4 flex-shrink-0" aria-hidden="true">
                    <Mail className="w-6 h-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1" role="heading" aria-level={4}>Email</h4>
                    <p className="text-gray-600">
                      <a href="mailto:arpan.physiocure@gmail.com" className="hover:underline focus:underline">arpan.physiocure@gmail.com</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start" role="listitem">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full mr-3 sm:mr-4 flex-shrink-0" aria-hidden="true">
                    <MapPin className="w-6 h-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1" role="heading" aria-level={4}>Location</h4>
                    <address className="text-gray-600 not-italic">
                      55, Ambikapuri, 60 Feet Road<br />
                      Aerodrome, Kalani Nagar, Raj Mohalla<br />
                      Indore - 452005, Madhya Pradesh
                    </address>
                    <a 
                      href="https://www.google.com/maps?q=22.72927838181346,75.81437693794295"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 font-semibold mt-1 inline-block focus:underline"
                      aria-label="Get directions to our clinic"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>

                <div className="flex items-start" role="listitem">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full mr-3 sm:mr-4 flex-shrink-0" aria-hidden="true">
                    <Clock className="w-6 h-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1" role="heading" aria-level={4}>Clinic Hours</h4>
                    <div className="text-gray-600">
                      <p>Mon–Sat: 09:00 AM–01:00 PM | 05:00 PM–09:00 PM</p>
                      <p>Sun: 10:00 AM–12:00 PM</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start" role="listitem">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-full mr-3 sm:mr-4 flex-shrink-0" aria-hidden="true">
                    <span className="w-6 h-6 text-blue-600 font-bold flex items-center justify-center">₹</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1" role="heading" aria-level={4}>Consultation Fee</h4>
                    <p className="text-gray-600">₹300/-</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Emergency Alert */}
            <div 
              className="bg-red-50 border-l-4 border-red-500 p-4 sm:p-6 rounded-r-lg"
              role="alert"
              aria-labelledby="emergency-heading"
            >
              <div className="flex items-center mb-3">
                <Zap className="w-6 h-6 text-red-500 mr-2" aria-hidden="true" />
                <h4 
                  id="emergency-heading"
                  className="font-bold text-red-700"
                  role="heading"
                  aria-level={4}
                >
                  Emergency Care
                </h4>
              </div>
              <p className="text-sm sm:text-base text-red-600 mb-4">
                For urgent injuries or severe pain requiring immediate attention.
              </p>
              <a 
                href="https://wa.me/917974434671"
                className="bg-red-500 hover:bg-red-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-colors duration-200 inline-block focus:ring-2 focus:ring-red-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
                aria-label="WhatsApp emergency number"
              >
                Emergency Contact
              </a>
            </div>

            {/* Map Integration */}
            <div className="bg-gray-200 rounded-lg h-48 sm:h-64 flex items-center justify-center mt-6" role="img" aria-label="Map showing clinic location">
              <iframe 
                title="Arpan PhysioCure™ Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.9720994390927!2d75.81437693794295!3d22.72927838181346!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd0781859ca5%3A0x1e833af01844040d!2sArpan%20PhysioCure%20by%20Dr%20Pragati%20And%20Dr%20Deep%20Maheshwari%20%2C%20Best%20Physiotherapy%20services%20in%20town!5e0!3m2!1sen!2sin!4v1754823575810!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Appointment Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8" role="main">
            <h3 
              className="text-xl sm:text-2xl font-bold text-gray-900 mb-2"
              role="heading"
              aria-level={3}
            >
              Or request via form (optional)
            </h3>
            <p className="text-sm text-gray-600 mb-6">Prefer us to contact you back? Fill this short form.</p>
            
            <form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              noValidate
              aria-label="Appointment booking form"
            >
              {/* Emergency Checkbox */}
              <div className="flex items-start p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                <input
                  type="checkbox"
                  id="emergency"
                  name="isEmergency"
                  checked={formData.isEmergency}
                  onChange={handleInputChange}
                  className="mr-2 sm:mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-red-300 rounded focus:ring-2 mt-0.5"
                  aria-describedby="emergency-description"
                />
                <label 
                  htmlFor="emergency" 
                  className="text-red-700 font-semibold text-sm sm:text-base"
                  id="emergency-description"
                >
                  This is an emergency appointment
                </label>
              </div>

              {/* Name */}
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base ${
                    hasFieldError('name') 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                  aria-invalid={hasFieldError('name')}
                  aria-describedby={hasFieldError('name') ? 'name-error' : undefined}
                />
                {hasFieldError('name') && (
                  <p 
                    id="name-error" 
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {getFieldError('name')}
                  </p>
                )}
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    htmlFor="email" 
                    className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base ${
                      hasFieldError('email') 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="your@email.com"
                    aria-invalid={hasFieldError('email')}
                    aria-describedby={hasFieldError('email') ? 'email-error' : undefined}
                  />
                  {hasFieldError('email') && (
                    <p 
                      id="email-error" 
                      className="mt-1 text-sm text-red-600"
                      role="alert"
                    >
                      {getFieldError('email')}
                    </p>
                  )}
                </div>
                <div>
                  <label 
                    htmlFor="phone" 
                    className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base ${
                      hasFieldError('phone') 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="+91 XXXXX XXXXX"
                    aria-invalid={hasFieldError('phone')}
                    aria-describedby={hasFieldError('phone') ? 'phone-error' : undefined}
                  />
                  {hasFieldError('phone') && (
                    <p 
                      id="phone-error" 
                      className="mt-1 text-sm text-red-600"
                      role="alert"
                    >
                      {getFieldError('phone')}
                    </p>
                  )}
                </div>
              </div>

              {/* Service Selection */}
              <div>
                <label 
                  htmlFor="service" 
                  className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                >
                  Service Required *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleInputChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base ${
                    hasFieldError('service') 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                  aria-invalid={hasFieldError('service')}
                  aria-describedby={hasFieldError('service') ? 'service-error' : undefined}
                >
                  <option value="">Select a service</option>
                  {services.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
                {hasFieldError('service') && (
                  <p 
                    id="service-error" 
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {getFieldError('service')}
                  </p>
                )}
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label 
                    htmlFor="preferredDate" 
                    className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                  >
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                    aria-describedby="date-help"
                  />
                  <p id="date-help" className="mt-1 text-xs text-gray-500">
                    Select your preferred appointment date
                  </p>
                </div>
                <div>
                  <label 
                    htmlFor="preferredTime" 
                    className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                  >
                    Preferred Time
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                    aria-describedby="time-help"
                  >
                    <option value="">Select time</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                  <p id="time-help" className="mt-1 text-xs text-gray-500">
                    Choose your preferred appointment time
                  </p>
                </div>
              </div>

              {/* Message */}
              <div>
                <label 
                  htmlFor="message" 
                  className="block text-sm sm:text-base font-medium text-gray-700 mb-2"
                >
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-vertical text-sm sm:text-base ${
                    hasFieldError('message') 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Describe your condition or any specific requirements..."
                  aria-invalid={hasFieldError('message')}
                  aria-describedby={hasFieldError('message') ? 'message-error message-help' : 'message-help'}
                />
                <p id="message-help" className="mt-1 text-xs text-gray-500">
                  Optional: Provide additional details about your condition or requirements
                </p>
                {hasFieldError('message') && (
                  <p 
                    id="message-error" 
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {getFieldError('message')}
                  </p>
                )}
              </div>

              {/* Submit + WhatsApp Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 flex items-center justify-center focus:ring-2 focus:ring-blue-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
                  aria-describedby="submit-help"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Book Appointment
                    </>
                  )}
                </button>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full border-2 border-green-600 text-green-700 hover:bg-green-50 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center focus:ring-2 focus:ring-green-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl">
                  WhatsApp Booking
                </a>
              </div>
              <p id="submit-help" className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our privacy policy and terms of service.
              </p>

              {/* Additional Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <button
                  type="button"
                  className="border-2 border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white py-2 sm:py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center focus:ring-2 focus:ring-blue-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
                  aria-label="Request home visit service"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Home Visit
                </button>
                <a
                  type="button"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-green-600 text-green-700 hover:bg-green-50 py-2 sm:py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center focus:ring-2 focus:ring-green-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
                  aria-label="Request telehealth consultation"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Book via WhatsApp
                </a>
              </div>
            </form>

            {/* Contact Alternative */}
            <div className="mt-8 pt-6 border-t text-center" role="complementary">
              <p className="text-sm sm:text-base text-gray-600 mb-4">Prefer to call? We're here to help!</p>
              <a 
                href="tel:+917974434671"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-full font-semibold transition-colors duration-200 inline-block focus:ring-2 focus:ring-blue-300 focus:outline-none text-sm sm:text-base shadow-lg hover:shadow-xl"
                aria-label="Call us directly at +91 7974434671"
              >
                Call +91 7974434671
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;